import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["products", "packs"];
import PortalIdParser from 'PortalIdParser';
import enviro from 'enviro';
import { stringify } from 'hub-http/helpers/params';
import { buildUrl, parseUrl } from 'hub-http/helpers/url';
import { getFullUrl } from 'hubspot-url-utils';
import devLogger from 'react-utils/devLogger';
export const createExternalUrl = (path = '', query, subdomain = 'app') => {
  const {
    protocol,
    hostname
  } = parseUrl(getFullUrl(subdomain));
  const urlOptions = {
    protocol,
    hostname,
    path,
    query: stringify(query || {})
  };
  return buildUrl(urlOptions);
};
export const getInterstitialV2Url = (purchaseMotion, query = {}, location = window.location) => {
  const isLocal = !enviro.deployed();
  const isOnPricingPage = location.pathname.includes('pricing');
  const shouldUseLocal = isLocal && isOnPricingPage;
  const portalId = PortalIdParser.get();
  const pathByMotion = {
    BUY_ADDITIONAL_SEATS: `/pricing-interstitial/${portalId}/seats`,
    BUY_ADDITIONAL_CONTACTS: `/pricing-interstitial/${portalId}/contacts`,
    BUY_TOUCHLESS: `/pricing-interstitial/${portalId}/purchase`
  };
  const path = pathByMotion[purchaseMotion];
  if (!path) {
    throw new Error(`Unsupported purchase motion: ${purchaseMotion}`);
  }
  const {
    protocol,
    hostname
  } = shouldUseLocal ? {
    protocol: 'https',
    hostname: location.hostname
  } : parseUrl(getFullUrl('app'));
  return buildUrl({
    protocol,
    hostname,
    path,
    query: stringify(query || {})
  });
};
export const getCheckoutUrl = (purchaseOrderId, query = {}) => {
  // Allows redirect to `local.hubspotqa` if environment is local, user is on pricing page, and localStorage set.
  const isLocal = !enviro.deployed();
  const isOnPricingPage = window.location.pathname.includes('pricing');
  let forceLocalRedirect = false;
  try {
    forceLocalRedirect = localStorage.getItem('CHECKOUT_REDIRECT_TO_LOCAL') === 'true';
  } catch (err) {
    forceLocalRedirect = false;
  }
  const shouldUseLocal = isLocal && isOnPricingPage && forceLocalRedirect;
  const url = createExternalUrl(`/checkout/${PortalIdParser.get()}/purchases/${purchaseOrderId}`, query, shouldUseLocal ? 'local' : 'app');
  return url;
};
const baseUrlOptions = () => {
  const isQAProxy = window.location.hostname.indexOf('wthubspot') >= 0;
  const isQA = enviro.isQa() || isQAProxy;
  const baseUrl = getFullUrl('app', {
    envOverride: isQA ? 'qa' : 'prod'
  });
  const {
    protocol,
    hostname
  } = parseUrl(baseUrl);
  return {
    protocol,
    hostname
  };
};
export const createPricingPageLink = (path = '', params, hash, isBeta = false) => {
  const portalId = PortalIdParser.get();
  if (params && !params.upgradeSource) {
    devLogger.warn({
      message: `createPricingPageLink: Expected upgradeSource for path ${path} but got ${params.upgradeSource}`,
      key: `createPricingPageLink: ${path}`
    });
  }
  const urlOptions = Object.assign({}, baseUrlOptions(), {
    path: `/${portalId ? 'pricing' : 'public-pricing'}${isBeta ? '-beta' : ''}/${portalId ? `${portalId}/` : ''}${path}`,
    query: params && stringify(params),
    hash: hash && hash.anchorLink ? hash.anchorLink : ''
  });
  return buildUrl(urlOptions);
};
export const createProductsAndAddonsLink = (path = '', params) => {
  const portalId = PortalIdParser.get();
  return buildUrl(Object.assign({}, baseUrlOptions(), {
    path: `/browse/${portalId}/${path}`,
    query: params && stringify(params)
  }));
};
export const navigateTo = (url, openInNewTab = false) => {
  if (window.hubspot.wordpress) {
    window.open(url, '_top');
  } else if (openInNewTab) {
    window.open(url, '_blank');
  } else {
    window.location.href = url;
  }
};
export const getSupportUrl = () => {
  return createExternalUrl(`/support-inbox/${PortalIdParser.get()}?source=in-app-help`);
};
export const getAccountDashboardUrl = () => createExternalUrl(`/account-and-billing/${PortalIdParser.get()}`);
export const getTrialUpgradePageUrl = apiName => {
  return buildUrl({
    path: `/trial-pricing/${PortalIdParser.get()}/upgrade`,
    query: `apiName=${apiName}`
  });
};
export const getUpgradePageUrl = (upgradeSource, params) => {
  return createPricingPageLink(`upgrade`, Object.assign({
    upgradeSource
  }, params));
};
export const CHECKOUT_PARAMS_KEY = '_checkoutParams';
export function getRelayedCheckoutParams(searchString = window.location.search) {
  try {
    const raw = new URLSearchParams(searchString).get(CHECKOUT_PARAMS_KEY);
    if (!raw) return undefined;
    return JSON.parse(raw);
  } catch (_unused) {
    return undefined;
  }
}
export function buildInterstitialUrl({
  purchaseMotion,
  apiName,
  purchaseRequest,
  trackingProperties,
  queryParams,
  productsAsQueryParams,
  interstitialQueryParams,
  redirectQueryParams
}) {
  var _ref2;
  const remainingQueryParams = _objectWithoutPropertiesLoose(queryParams, _excluded);
  const checkoutParamsEntry = redirectQueryParams && Object.keys(redirectQueryParams).length > 0 ? {
    [CHECKOUT_PARAMS_KEY]: JSON.stringify(redirectQueryParams)
  } : {};
  if (purchaseMotion === 'BUY_ADDITIONAL_SEATS' || purchaseMotion === 'BUY_ADDITIONAL_CONTACTS') {
    var _ref;
    return getInterstitialV2Url(purchaseMotion, Object.assign({}, trackingProperties !== null && trackingProperties !== void 0 && trackingProperties.upgradeSource ? {
      upgradeSource: String(trackingProperties.upgradeSource)
    } : {}, {
      purchaseMotion
    }, remainingQueryParams, (_ref = interstitialQueryParams) !== null && _ref !== void 0 ? _ref : {}, checkoutParamsEntry));
  }
  return getInterstitialV2Url(purchaseMotion, Object.assign({
    primaryProduct: apiName
  }, productsAsQueryParams, purchaseRequest.paymentTerm ? {
    paymentTerm: String(purchaseRequest.paymentTerm).toLowerCase()
  } : {}, purchaseRequest.commitmentTerm ? {
    commitmentTerm: String(purchaseRequest.commitmentTerm).toLowerCase()
  } : {}, trackingProperties !== null && trackingProperties !== void 0 && trackingProperties.upgradeSource ? {
    upgradeSource: String(trackingProperties.upgradeSource)
  } : {}, {
    purchaseMotion
  }, remainingQueryParams, purchaseRequest.currencyCode ? {
    currencyCode: String(purchaseRequest.currencyCode)
  } : {}, (_ref2 = interstitialQueryParams) !== null && _ref2 !== void 0 ? _ref2 : {}, checkoutParamsEntry));
}